import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Percent, Tag, Mail, Clock, ArrowRight } from 'lucide-react';
import { usePortalAuth } from '@/hooks/usePortalAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const DEFAULT_MEDALLION_PRICE_CENTS = 2500;

const PortalRates = () => {
  const navigate = useNavigate();
  const { isOwner, isLoading: authLoading, company } = usePortalAuth();
  const [pontisOrderRate, setPontisOrderRate] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isOwner) {
      toast.error('You do not have permission to view rates');
      navigate('/portal/dashboard');
    }
  }, [authLoading, isOwner, navigate]);

  useEffect(() => {
    if (!isOwner || authLoading || !company?.id) return;

    const fetchRates = async () => {
      try {
        // Fetch Pontis order rate from linked fulfillment partner (self-fulfillment)
        if (company.linked_fulfillment_partner_id) {
          const { data } = await supabase
            .from('fulfillment_partners')
            .select('pontis_order_rate')
            .eq('id', company.linked_fulfillment_partner_id)
            .single();
          if (data) setPontisOrderRate(data.pontis_order_rate);
        }
      } catch (err) {
        console.error('Error fetching rates:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRates();
  }, [company?.id, company?.linked_fulfillment_partner_id, isOwner, authLoading]);

  if (authLoading || isLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-36" />
        <Skeleton className="h-28" />
      </div>
    );
  }

  if (!isOwner) return null;

  const commissionRate = company?.commission_rate ?? null;
  const medallionPriceCents = company?.medallion_unit_price_cents ?? DEFAULT_MEDALLION_PRICE_CENTS;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Rates & Pricing</h1>
        <p className="text-muted-foreground">
          Your agreed-upon rates with Pontis Life
        </p>
      </div>

      {/* Rate Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* Commission rate — partner-fulfilled services */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Your Commission Rate</CardTitle>
            <Percent className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {commissionRate !== null
                ? `${(commissionRate * 100).toFixed(0)}%`
                : '—'}
            </div>
            <p className="text-xs text-muted-foreground">
              Of service value when partner fulfills cleanings
            </p>
          </CardContent>
        </Card>

        {/* Pontis cut — self-fulfilled cleanings */}
        {company?.linked_fulfillment_partner_id && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pontis Cleaning Cut</CardTitle>
              <Percent className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {pontisOrderRate !== null
                  ? `${(pontisOrderRate * 100).toFixed(0)}%`
                  : '—'}
              </div>
              <p className="text-xs text-muted-foreground">
                Pontis retains this % when you self-fulfill cleanings
              </p>
            </CardContent>
          </Card>
        )}

        {/* Medallion pricing */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Medallion Price</CardTitle>
            <Tag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${(medallionPriceCents / 100).toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">Per unit — minimum order of 10</p>
          </CardContent>
        </Card>
      </div>

      {/* Payment Terms */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">Payment Terms</CardTitle>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-sm leading-relaxed">
            All payouts — commission earnings from partner-fulfilled services and self-fulfillment cleaning
            proceeds — are issued every two weeks. Payments are processed via ACH transfer to your account on file.
          </CardDescription>
        </CardContent>
      </Card>

      {/* Billing Contact */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">Billing Questions</CardTitle>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-sm leading-relaxed">
            For questions about your rates, invoices, or payouts, contact the Pontis billing team at{' '}
            <a
              href="mailto:support@pontis.life"
              className="text-foreground underline underline-offset-4 hover:text-primary"
            >
              support@pontis.life
            </a>
            .
          </CardDescription>
        </CardContent>
      </Card>

      {/* Fulfillment Portal cross-link — only for dual-role companies */}
      {company?.linked_fulfillment_partner_id && (
        <Card className="border-l-4 border-l-[#787559] bg-[#FFFEF5]">
          <CardContent className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6">
            <div>
              <h3 className="text-base font-semibold text-[#212121]">
                Looking for your fulfillment pricing?
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                View and manage your headstone cleaning product rates in the Fulfillment Portal.
              </p>
            </div>
            <Button
              onClick={() => navigate('/partner/pricing')}
              className="bg-[#787559] hover:bg-[#787559]/90 text-white shrink-0"
            >
              View Fulfillment Pricing
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PortalRates;
